//@ts-strict-ignore
import { API_DATA, META_DATA } from '../../middleware/apiService';
import types from './types';
import { AlertType, IAlert, Maybe } from '../../../graphql/schema/p4types';
import { ReactElement } from 'react';

export function requestInstance(entity, dataStoreId, id, customUrl, messageDefinition) {
  return requestCriteriaInstance(entity, dataStoreId, { id: id }, customUrl, messageDefinition);
}

export function requestCriteriaInstance(entity, dataStoreId, criteria, customUrl, messageDefinition) {
  const params: { requestBody: { instanceCriteria: any; messageDefinition?: any } } = {
    requestBody: { instanceCriteria: criteria },
  };
  if (messageDefinition) {
    params.requestBody.messageDefinition = messageDefinition;
  }

  return {
    [API_DATA]: {
      types: [types.REQUEST_INSTANCE_REQUEST, types.REQUEST_INSTANCE_SUCCESS, types.REQUEST_INSTANCE_FAILURE],
      endpoint: customUrl ? customUrl : `${entity}/read`,
      params: params,
    },
    [META_DATA]: {
      dataStoreId: dataStoreId,
    },
  };
}

type IQueryParams = {
  orderBy?: Array<{ fieldName: string; isDescending: boolean }>;
  pageSize?: number;
  pageNumber?: number;
  genericFilter?: string;
  filters?: any; //Could this be Array<IFilterInput?>
};

export function generateRequestBody(params) {
  const isDesc = params.sortDir !== 'ASC';
  const queryParams: IQueryParams = {};
  if (params.sortBy) {
    queryParams.orderBy = [{ fieldName: params.sortBy, isDescending: isDesc }];
  }
  if (params.pageNo && params.pageSize) {
    queryParams.pageSize = params.pageSize;
    queryParams.pageNumber = params.pageNo;
  }
  if (params.filter) queryParams.genericFilter = params.filter;

  if (params.filters) {
    queryParams.filters = params.filters;
  }

  const requestBody: { queryParameters: IQueryParams; messageDefinition?: Array<string> } = {
    queryParameters: queryParams,
  };
  if (params.messageDefinition) {
    requestBody.messageDefinition = params.messageDefinition;
  } else {
    requestBody.messageDefinition = ['*'];
  }
  return requestBody;
}

export function requestList(entity, dataStoreId, params, code) {
  const requestBody = generateRequestBody(params);

  const dataIndex = params.pageNo ? params.pageNo * params.pageSize - params.pageSize : 0;
  return {
    [API_DATA]: {
      types: [types.REQUEST_LIST_REQUEST, types.REQUEST_LIST_SUCCESS, types.REQUEST_LIST_FAILURE],
      endpoint: `${entity}/query`,
      params: {
        requestBody: requestBody,
      },
    },
    [META_DATA]: {
      dataStoreId: dataStoreId,
      dataIndex: dataIndex,
      code: code,
      replaceData: typeof params.replaceData !== 'undefined' ? params.replaceData : true,
    },
  };
}

export function saveInstance(alertText, entity, instance, dataStoreId) {
  var requestBody = {
    instance,
  };
  return {
    [API_DATA]: {
      types: [types.SAVE_INSTANCE_REQUEST, types.SAVE_INSTANCE_SUCCESS, types.SAVE_INSTANCE_FAILURE],
      endpoint: `${entity}/save`,
      params: {
        requestBody,
      },
    },
    [META_DATA]: {
      alertText: alertText,
      dataStoreId: dataStoreId,
    },
  };
}

export function requestScreenDetails(entity, screenName) {
  return {
    [API_DATA]: {
      types: [types.SCREEN_DETAILS_REQUEST, types.SCREEN_DETAILS_SUCCESS, types.SCREEN_DETAILS_FAILURE],
      endpoint: `${entity}/screenDetails`,
      params: {
        requestBody: {
          actionCriteria: { screenName: screenName },
          messageDefinition: ['**'],
        },
      },
    },
    [META_DATA]: {
      dataStoreId: null,
      screenName: screenName,
      entity: entity,
    },
  };
}

export function dismissSystemAlert(id) {
  return {
    type: types.DISMISS_SYSTEM_ALERT,
    [META_DATA]: {
      alertId: id,
    },
  };
}

export function dismissConfirmationPrompt() {
  return { type: types.DISMISS_CONFIRMATION_PROMPT };
}

/**
 * -- Alerts --
 * @param title
 * @param errorId
 * @param alertText
 * @param alertType
 * @param alertField
 * @param alertForm
 * @param status
 * @param confirmationReference
 * @param uuid
 * @param responses
 * @param mutationProps - Mutation & associated props for the purpose of re-firing a mutation which was not the regular submit query (for Confirmation Prompts).
 * @param customAlertId - Custom ID to use for alert for the purpose of 'clearing' the alert when an action is completed.
 * @param alert - Alert in services format (I.e. The format passed by EntityMetaData).
 * @deprecated - Please access the P4 toast controller to dispatch notifications
 *  const \{dispatchSimpleToast\} = useP4ToastController();
 *  dispatchSimpleToast(\'title\', \'message\', AlertType);
 *  other toast types are available e.g dispatchToast, dispatchBlockingToast, dispatchJobToast, dispatchDeprecatedToast
 */
export function genericAlert(
  title: string | null = null,
  alertText?: string,
  alertType: AlertType = AlertType.Error,
  {
    //Converted to named parameters via destructuring as usually using a subset of these.
    alertField = null,
    alertForm = null,
    status = null,
    confirmationReference = null,
    uuid = null,
    responses = null,
    mutationProps = null,
    customAlertId = `${Date.now()}-${alertType}`,
    alert, //Alert in services representation (Alert.java) - Would be good to migrate text/type/title to use this in the future.
  }: {
    alertField?: string | null;
    alertForm?: string | null;
    status?: string | null;
    confirmationReference?: string | null;
    uuid?: string | null;
    responses?: any;
    mutationProps?: any;
    customAlertId?: string;
    alert?: IAlert;
  } = {},
  errorId?: string
) {
  return {
    type: types.GENERIC_ALERT,
    [META_DATA]: {
      alertType: alertType,
      alertText: alertText,
      alertField: alertField,
      alertForm: alertForm,
      status: status,
      confirmationReference: confirmationReference,
      uuid: uuid,
      alertTitle: title,
      alertId: alert?.uuid || customAlertId,
      responses: responses,
      mutationProps: mutationProps,
      alert: alert,
      errorId: errorId,
    },
  };
}

export function actionAlert(
  alertTitle: string | null = null,
  alertText: string | null = null,
  alertType = types.ACTION_ALERT,
  notificationActionId = null,
  notificationActionType: Maybe<string> = null,
  alertId = `${Date.now()}-${alertType}`,
  { alert }: { alert?: IAlert } = {}
) {
  const metaData = {
    alertId: alertId,
    alertTitle: alertTitle,
    alertText: alertText,
    alertType: alertType,
    notificationActionId: notificationActionId,
    notificationActionType: notificationActionType,
    alert: alert,
  };
  return {
    type: types.ACTION_ALERT,
    [META_DATA]: metaData,
  };
}

export function isScreenDetailsCached(entity, screenName, loadViews) {
  return (dispatch, getState) => {
    const describe = getState().data.describe;
    if (!describe || !describe[entity] || loadViews) {
      return dispatch(() => dispatch(requestScreenDetails(entity, screenName)));
    }
    return Promise.resolve(true);
  };
}

export function genericAction(endpoint, requestBody, metaData, messageDefinition, method) {
  if (messageDefinition) {
    requestBody.messageDefinition = messageDefinition;
  }
  return {
    [API_DATA]: {
      types: [types.GENERIC_REQUEST, types.GENERIC_SUCCESS, types.GENERIC_FAILURE],
      method: !method ? 'POST' : 'GET',
      endpoint: endpoint,
      params: {
        requestBody: requestBody,
      },
    },
    [META_DATA]: metaData,
  };
}

export function saveCustomMenu(actionCriteria, messageDefinition = ['**']) {
  return {
    [API_DATA]: {
      types: [types.SAVE_MENU_REQUEST, types.SAVE_MENU_SUCCESS, types.GENERIC_FAILURE],
      endpoint: '/system/Screen/updateCustomMenu',
      params: {
        requestBody: {
          actionCriteria: actionCriteria,
          messageDefinition: messageDefinition,
        },
      },
    },
    [META_DATA]: {
      alertText: 'Custom menu saved',
    },
  };
}

export function setApiCode(dataStoreId, code) {
  return { type: types.SET_API_CODE, dataStoreId, code };
}

export function switchCompany(systemCompany) {
  return { type: types.SWITCH_COMPANY_SUCCESS, systemCompany };
}

export default {
  requestInstance,
  requestCriteriaInstance,
  requestList,
  saveInstance,
  dismissSystemAlert,
  dismissConfirmationPrompt,
  genericAlert,
  isScreenDetailsCached,
  genericAction,
  saveCustomMenu,
  setApiCode,
  actionAlert,
  switchCompany,
};
